import { useState, useEffect, useRef } from "react";
import './CustomDropdown.css';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import useApp from "../AppContext";
import { t } from "./../i18n"

/**
 * CustomDropdown - Styled dropdown component που λειτουργεί σαν το Form.Control select
 * 
 * @param {Array} options - Array με {value, label} objects ή απλά strings
 * @param {*} value - Current selected value
 * @param {Function} onChange - Callback (event, selectedIndex) => {}
 * @param {boolean} readonly - Readonly state
 * @param {boolean} disabled - Disabled state
 * @param {string} placeholder - Placeholder text
 * @param {boolean} showEmptyOption - Show empty/null option at top
 * @param {string} className - Additional CSS classes
 */
export default function CustomDropdown({
    options = [],
    value,
    onChange,
    readonly = false,
    disabled = false,
    placeholder = "Επιλέξτε...",
    showEmptyOption = true,
    className = ""
}) {
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);
    const isDisabled = readonly || disabled;
    const app = useApp();
    const [language] = useState(app.sessionToken?.Language);

    // Normalize options to {value, label} format
    const normalizedOptions = options.map(opt => {
        if (typeof opt === 'string') {
            return { value: opt, label: opt };
        }
        return opt;
    });

    const handleSelect = (option, index) => {
        // Create synthetic event similar to Form.Control select
        const syntheticEvent = {
            target: {
                value: option.value,
                selectedIndex: index
            },
            currentTarget: {
                value: option.value,
                selectedIndex: index
            }
        };

        onChange(syntheticEvent, index);
        setOpen(false);
    };

    const selectedOption = normalizedOptions.find(o => o.value === value);

    // Click outside handler
    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setOpen(false);
            }
        }

        if (open) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [open]);

    // Keyboard navigation
    useEffect(() => {
        function handleKeyDown(event) {
            if (!open) return;

            if (event.key === 'Escape') {
                setOpen(false);
            } else if (event.key === 'ArrowDown') {
                event.preventDefault();
                // TODO: Navigate to next option
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                // TODO: Navigate to previous option
            }
        }

        if (open) {
            document.addEventListener("keydown", handleKeyDown);
        }

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [open]);

    return (
        <div
            className={`custom-dropdown-wrapper ${className}`}
            ref={wrapperRef}
        >
            <div
                className={`custom-dropdown-display ${isDisabled ? "disabled" : ""} ${open ? "open" : ""}`}
                onClick={() => !isDisabled && setOpen(!open)}
                role="button"
                tabIndex={isDisabled ? -1 : 0}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className="dropdown-value">
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <span className="dropdown-arrow">
                    <KeyboardArrowDownOutlinedIcon />
                </span>
            </div>

            {open && (
                <ul
                    className="custom-dropdown-options"
                    role="listbox"
                >
                    {showEmptyOption && (
                        <li
                            onClick={() => handleSelect({ value: null, label: "" }, 0)}
                            className={value === null || value === undefined || value === "" ? "selected" : ""}
                            role="option"
                            aria-selected={value === null || value === undefined}
                        >
                            <em style={{ opacity: 0.5,  fontSize: '0.9rem', fontStyle: 'italic' }}>-- {t('common.selectOne', language)} --</em>
                        </li>
                    )}
                    {normalizedOptions.map((option, i) => {
                        const actualIndex = showEmptyOption ? i + 1 : i;
                        const isSelected = option.value === value;

                        return (
                            <li
                                key={i}
                                onClick={() => handleSelect(option, actualIndex)}
                                className={isSelected ? "selected" : ""}
                                role="option"
                                aria-selected={isSelected}
                            >
                                {option.label}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
